
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {CURSOR_MARKER_COLOR} from "../CursorPointMarker.js";
import {createMarker, createDashedLine, setLinePositions} from "./perpendicularCapture.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {captureFixedPoints} from "./pipeTool.js";

// Shared 3-click capture flow for every "digitalisér nødvendige
// referencepunkter -> grundgeometri er kendt -> popup åbner" parametric
// construction tool (Cirkel, Rektangel, direct Bue - task DEL A/B/C/D) -
// one shared implementation rather than three near-identical copies.
// Reuses createMarker()/createDashedLine()/setLinePositions() (already
// shared by the Vinkelret/Parallelforskydning tool family - see
// perpendicularCapture.js's own doc comment) and pipeTool.js's own
// captureFixedPoints() (the "N fixed left-clicks, live per-click callback"
// primitive Rør already uses for its 2 direction + 5 seed points).
//
// The preview markers are deliberately lightweight: sized ONCE at the
// moment each point is placed (not continuously rescaled every frame the
// way a committed FeatureVisual is) - acceptable for a capture phase that
// only lasts a few clicks; the REAL, continuously-scaled visual takes over
// immediately afterwards once the tool registers its actual feature and
// opens its live-preview dialog.

const PREVIEW_COLOR = 0xffdc00; // matches every other construction-preview in this codebase (Midpoint, Midline, Pipe, Vinkelret/Parallelforskydning)

export function toVector3Array(points){
	return points.map((p) => new THREE.Vector3(p.x, p.y, p.z));
}

export function toPlainPoint(v){
	return {x: v.x, y: v.y, z: v.z};
}

// `instructionForPoint(pointCountAlreadyPlaced_or_0)` -> instruction string
// for "please place point N" (1-indexed, called with 1 before the first
// click and with N+1 after the Nth click lands, matching
// captureFixedPoints()'s own `getInstructionText` contract). Returns
// Promise<THREE.Vector3[]|null> - null on ESC/cancel, otherwise exactly 3
// points in click order.
export function captureThreePointsWithPreview(dm, tool, instructionForPoint){
	const scene = dm.scene;
	const markers = [createMarker(CURSOR_MARKER_COLOR), createMarker(CURSOR_MARKER_COLOR), createMarker(CURSOR_MARKER_COLOR)];
	const lines = [createDashedLine(PREVIEW_COLOR), createDashedLine(PREVIEW_COLOR)];
	for(const m of markers) scene.add(m);
	for(const l of lines) scene.add(l);

	const sizeMarker = (marker, worldPos) => {
		const camera = dm.viewer.scene.getActiveCamera();
		const size = dm.renderer.getSize(new THREE.Vector2());
		const cameraPos = camera.getWorldPosition(new THREE.Vector3());
		const s = computeScreenSpaceScale(camera, worldPos, cameraPos, size.width, size.height, ScreenSpaceSizes.computedPoint);
		marker.scale.setScalar(s);
	};

	const dispose = () => {
		for(const m of markers){ scene.remove(m); m.geometry.dispose(); m.material.dispose(); }
		for(const l of lines){ scene.remove(l); l.geometry.dispose(); l.material.dispose(); }
	};

	dm._showInstruction(tool.name, instructionForPoint(1));

	return captureFixedPoints(dm, tool, 3, instructionForPoint, (point, count) => {
		const marker = markers[count - 1];
		marker.position.copy(point);
		marker.visible = true;
		sizeMarker(marker, point);

		if(count >= 2){
			setLinePositions(lines[count - 2], markers[count - 2].position, point);
			lines[count - 2].visible = true;
		}
	}).then((points) => {
		dispose();
		return points;
	});
}
